import { computed, nextTick, ref, watch, type Ref } from 'vue';
import dayjs from '~/utils/dayjs';
import { formatTz, getClientTz } from '~/utils/date';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useGroupSendingCalendar } from './useGroupSendingCalendar';
import { useGroupMessageRules } from './useGroupMessageRules';

interface UseQuickBlastWhenOptions {
    campaignType: Ref<'marketing' | 'transactional' | undefined>;
    modelValue: Ref<Date | string | undefined>;
    currentTab?: Ref<'now' | 'later'>;
}

interface UseQuickBlastWhenReturn {
    date: Ref<Date | string | undefined>;
    canScheduleNow: Ref<boolean>;
    initialCalendarLoading: Ref<boolean>;
    dateFormatted: Ref<string | null>;
    tzFormatted: Ref<string>;
    dateWithTzFormatted: Ref<string | null>;
    campaignTypeRestricted: Ref<boolean>;
    calculateCanScheduleNow: () => boolean;
    loadCurrentMonthCalendar: () => Promise<void>;
    roundDateToNearestFiveMinutes: (date?: string) => void;
}

export function useQuickBlastWhen(
    options: UseQuickBlastWhenOptions,
    emit?: (event: 'update:modelValue', value: Date | string | undefined) => void
): UseQuickBlastWhenReturn {
    const { currentGroup } = useCurrentGroup();
    const { isTypeRestricted } = useGroupMessageRules();
    const { getSendingCalendar, allowedHoursByDate, disabledDates, isMonthLoaded, findNextAvailableTime } =
        useGroupSendingCalendar();

    const { campaignType, modelValue, currentTab } = options;

    const date = ref<Date | string | undefined>(modelValue.value);

    const campaignTypeRestricted = computed(() => (campaignType.value ? isTypeRestricted(campaignType.value) : false));

    const initialCalendarLoading = ref<boolean>(
        !!currentGroup.value?.group_id && !modelValue.value && campaignTypeRestricted.value
    );
    const canScheduleNow = ref<boolean>(!currentGroup.value?.group_id || !campaignTypeRestricted.value);

    const calculateCanScheduleNow = (): boolean => {
        if (!currentGroup.value?.group_id || !campaignTypeRestricted.value) {
            return true;
        }

        const tz = getClientTz();
        const now = dayjs().tz(tz);
        const dateKey = now.format('YYYY-MM-DD');
        const currentHour = now.hour();
        const currentMonth = { year: now.year(), month: now.month() + 1 };

        if (!isMonthLoaded(currentMonth)) {
            return false;
        }

        const isDateDisabled = disabledDates.value.some(
            (disabledDate) => dayjs(disabledDate).tz(tz).format('YYYY-MM-DD') === dateKey
        );

        if (isDateDisabled) {
            return false;
        }

        const allowedHours = allowedHoursByDate.value.get(dateKey);
        if (allowedHours && allowedHours.length > 0) {
            return allowedHours.includes(currentHour);
        }

        return true;
    };

    const dateFormatted = computed(() =>
        date.value && date.value !== 'now'
            ? dayjs(date.value).tz(getClientTz()).format('MMM DD, YYYY [at] h:mm a')
            : null
    );
    const tzFormatted = computed(() => formatTz(dayjs().tz(getClientTz()).format('z')));
    const dateWithTzFormatted = computed(() =>
        date.value && date.value !== 'now' ? dateFormatted.value + ' ' + tzFormatted.value : null
    );

    const loadCurrentMonthCalendar = async () => {
        if (!currentGroup.value?.group_id || !campaignType.value || !campaignTypeRestricted.value) {
            return;
        }

        initialCalendarLoading.value = true;
        const now = dayjs().tz(getClientTz());
        const activeMonth = { year: now.year(), month: now.month() + 1 };
        await getSendingCalendar(currentGroup.value.group_id, campaignType.value, activeMonth);

        canScheduleNow.value = calculateCanScheduleNow();
        await nextTick();
        initialCalendarLoading.value = false;
    };

    const roundDateToNearestFiveMinutes = (dateToTransform?: string) => {
        const transformedDate = dateToTransform ? dayjs(dateToTransform).tz(getClientTz()) : dayjs().tz(getClientTz());
        const minutes = transformedDate.minute();
        const roundedMinutes = minutes >= 56 ? Math.floor(minutes / 5) * 5 : Math.ceil(minutes / 5) * 5;

        date.value = transformedDate.minute(roundedMinutes).toDate();
    };

    watch(
        () => modelValue.value,
        (newModelValue) => {
            date.value = newModelValue;
        },
        { immediate: true }
    );

    if (emit) {
        watch(date, (newDate) => emit('update:modelValue', newDate));
    }

    if (currentTab) {
        watch(currentTab, (newCurrentTab, oldCurrentTab) => {
            if (newCurrentTab === 'now') {
                date.value = 'now';
            } else if (newCurrentTab === 'later') {
                if (currentGroup.value?.group_id && campaignTypeRestricted.value) {
                    if (oldCurrentTab === 'now' && canScheduleNow.value) {
                        date.value = findNextAvailableTime();
                    } else {
                        date.value = undefined;
                    }
                } else {
                    const now = dayjs().tz(getClientTz());
                    const minutes = now.minute();
                    const roundedMinutes = Math.ceil(minutes / 5) * 5;
                    date.value = now.minute(roundedMinutes).toDate();
                }
            }
        });
    }

    return {
        date,
        canScheduleNow,
        initialCalendarLoading,
        dateFormatted,
        tzFormatted,
        dateWithTzFormatted,
        campaignTypeRestricted,
        calculateCanScheduleNow,
        loadCurrentMonthCalendar,
        roundDateToNearestFiveMinutes,
    };
}
